/**
 * Card flights — the shared fly-home/fly-to-slot ghosts for released cards.
 * A flight is launched whenever a carried card is released (from the hand or
 * from a staged cell) and homes onto wherever the card now lives, tracked
 * frame by frame by test id: the staged cell card after a placement, the hand
 * slot after a miss or an unplan, or nowhere (a played spell) — then it just
 * parks and dissolves. GameView owns the list; Hand and Cell only launch.
 */
import { useEffect, useRef } from 'react';
import type { CSSProperties, ReactElement } from 'react';
import type { CellId, InstanceId } from '@cards/engine';
import {
  cardDragMotionVariables,
  setCardDragTarget,
  startCardDragAnimation,
  type CardDragMotion,
} from './cardMotion.js';
import { HandCardFace } from './HandCardFace.js';
import { statLen } from './BoardCard.js';
import { CardGlyph } from './CardGlyph.js';
import { ScrollingCardName } from './ScrollingCardName.js';
import { CardBackFace } from './CardBackGlyph.js';
import { StatChip } from './StatChip.js';
import { cardName } from './locale.js';
import type { HandCardData } from './Hand.js';
import { MOTION_SPRINGS } from './kit/tokens.js';

/** Fallback preview anchor (translate(-50%, -100% - 18px)) when it cannot be measured. */
export const PREVIEW_ANCHOR_GAP = 18;
/** Hard ceiling for a flight so a stuck spring can never strand the ghost. */
const FLIGHT_DEADLINE_MS = MOTION_SPRINGS.flightDeadlineMs;
/** A flight whose card has no visible home (a cast spell) parks after this. */
const FLIGHT_NO_TARGET_MS = MOTION_SPRINGS.flightNoTargetMs;
/** Dissolve of the parked card over its destination — starts only once it has stopped. */
const FLIGHT_LANDING_FADE_MS = MOTION_SPRINGS.flightLandingFadeMs;
/** Rate (per second) at which a flying card's face eases into the field face. */
const FACE_FOLLOW_RATE = 15;
/**
 * A dealt card rises face-down and turns over on the way in: it starts to turn
 * once it is clear of the screen edge and is face-up well before it lands, so
 * the player reads what they drew in the air rather than after it has settled.
 */
export const DEAL_TURN_DELAY_MS = 90;
export const DEAL_TURN_MS = 340;

/**
 * How far over a dealt card has turned, 0→180°, eased in and out so the turn is
 * fastest through edge-on — where the back gives way to the face — like a flick.
 */
export function dealTurn(elapsedMs: number): number {
  const progress = Math.min(1, Math.max(0, (elapsedMs - DEAL_TURN_DELAY_MS) / DEAL_TURN_MS));
  const eased = progress < .5 ? 4 * progress ** 3 : 1 - (-2 * progress + 2) ** 3 / 2;
  return eased * 180;
}

/** Measured box and anchor of the live drag preview at the moment of release. */
export interface FlightOrigin {
  width: number;
  height: number;
  anchorGap: number;
  /** Starts face-down and turns over in flight: a card being dealt from the deck. */
  dealt?: boolean;
}

/** The semantic home selected by the completed drop gesture. */
export type CardFlightDestination =
  | { kind: 'auto' }
  | { kind: 'hand' }
  | { kind: 'staged'; cellId: CellId }
  /** A resolved board occupant (sandbox placements/moves land on `board-card-<id>`). */
  | { kind: 'board' }
  | { kind: 'none' };

/**
 * One independent flight. Deliberately mutable: the rAF loop advances
 * `motion`/`sizeProgress`/`startDistance` in place.
 */
export interface CardFlight {
  id: number;
  card: HandCardData;
  motion: CardDragMotion;
  deadline: number;
  fromWidth: number;
  fromHeight: number;
  toWidth: number;
  toHeight: number;
  /** Measured preview anchor (px below its bottom edge) — 18 desktop, 12 mobile. */
  anchorGap: number;
  /** Release→destination distance, measured when the target first exists (-1 until then). */
  startDistance: number;
  sizeProgress: number;
  /** Box actually painted this frame; glides toward the travel-morph target. */
  renderWidth: number;
  renderHeight: number;
  /**
   * The size the compact face is currently LAID OUT at — its fixed box, not the
   * painted one. It follows the measured destination (a staged card appears late,
   * the hand re-flows), and every change to it is a real layout of that face, so
   * it moves in the few steps the destination actually takes rather than every
   * frame: the frame-by-frame resize is the face's transform, not its box.
   */
  compactWidth: number;
  compactHeight: number;
  /** Previous rAF timestamp for the time-based box glide. */
  lastFrameAt: number | undefined;
  /** 0→1 morph toward the compact staged look; eased in the rAF loop. */
  faceProgress: number;
  /** Parked on the destination: the ghost dissolves while it un-hides underneath. */
  landing: boolean;
  /** Destination candidates by selector, first visible one wins. */
  targetSelectors: readonly string[];
  /**
   * Whether any candidate destination is a compact one, so the ghost needs the
   * field face to cross-fade into at all. A card flying back to the hand or a
   * cast spell parking in place never shows it — and mounting it anyway cost a
   * second full card, second glyph included, inside the subtree whose box the
   * rAF loop rewrites every frame.
   */
  canLandCompact: boolean;
  /**
   * The resolved destination, kept between frames. Its box is still re-measured
   * every frame (the hand re-flows, a staged card appears late), but the selector
   * scan behind it does not have to run again. `compact` travels with the element
   * because an `auto` flight has candidates of both kinds and only the one that
   * actually matched may drive the face morph.
   */
  target: { element: HTMLElement; compact: boolean } | undefined;
  /** An acknowledged move waits for its exact destination instead of parking early. */
  waitForTarget: boolean;
  /** Set while no destination exists; parks the flight after FLIGHT_NO_TARGET_MS. */
  noTargetSince: number | undefined;
  /** Face-down at launch, turned over by the loop (see dealTurn). */
  dealt: boolean;
  launchedAt: number;
}

let flightSeq = 0;

export function isCompactCardFlightTarget(selector: string): boolean {
  return selector.includes('staged-card-')
    || selector.includes('data-flight-staged-id')
    || selector.includes('board-card-');
}

export function createCardFlight(
  card: HandCardData,
  motion: CardDragMotion,
  origin: FlightOrigin,
  destination: CardFlightDestination = { kind: 'auto' },
): CardFlight {
  motion.returning = true;
  // The ghost's CSS drops the drag preview's +.04 scale bonus, so bake it into
  // the motion here: the visual size is continuous at the handover and the
  // spring then relaxes to exactly 1 — pixel-identical to the destination.
  motion.scaleX += 0.04;
  motion.scaleY += 0.04;
  flightSeq += 1;
  const now = globalThis.performance?.now() ?? Date.now();
  const targetSelectors: readonly string[] = destination.kind === 'staged'
    ? [
        `[data-testid="cell-${destination.cellId}"] [data-testid="staged-card-${card.instance.id}"]`,
        `[data-testid="cell-${destination.cellId}"] [data-flight-staged-id="${card.instance.id}"]`,
      ]
    : destination.kind === 'hand'
      ? [`[data-testid="hand-card-${card.instance.id}"]`]
      : destination.kind === 'board'
        ? [`[data-testid="board-card-${card.instance.id}"]`]
        : destination.kind === 'none'
          ? []
          : [
              `[data-testid="staged-card-${card.instance.id}"]`,
              `[data-testid="hand-card-${card.instance.id}"]`,
            ];
  return {
    id: flightSeq,
    card,
    motion,
    deadline: now + FLIGHT_DEADLINE_MS,
    fromWidth: origin.width,
    fromHeight: origin.height,
    toWidth: origin.width,
    toHeight: origin.height,
    anchorGap: origin.anchorGap,
    startDistance: -1,
    sizeProgress: 0,
    renderWidth: origin.width,
    renderHeight: origin.height,
    compactWidth: origin.width,
    compactHeight: origin.height,
    lastFrameAt: undefined,
    faceProgress: 0,
    landing: false,
    targetSelectors,
    // Decided once, from the same selectors the loop matches against, so the
    // mounted face and the face the morph can reach cannot drift apart.
    canLandCompact: targetSelectors.some(isCompactCardFlightTarget),
    target: undefined,
    waitForTarget: destination.kind === 'staged' || destination.kind === 'hand' || destination.kind === 'board',
    noTargetSince: undefined,
    dealt: origin.dealt === true,
    launchedAt: now,
  };
}

/**
 * A released card flying to its destination. It keeps the drag spring (no
 * interruption on landing or when another card is picked up), morphs toward the
 * measured destination size and dissolves over it once genuinely parked — never
 * mid-drift.
 *
 * Nothing here resizes a box per frame. The ghost element is a fixed-size
 * transform carrier (the release footprint) and the card is three fixed-size
 * layers under it — shadow plate, hand face, compact field face — which the loop
 * below only ever scales and fades. See the data-return-morph block in KIT_CSS
 * for why: the ghost used to write its own width/height on every frame, and that
 * is a real layout of a whole hand card plus a compact face that is a size
 * container with cqh-driven stats. Now each face is laid out once, its container
 * query resolves once, and a flight costs one destination measurement a frame.
 */
export function CardFlightGhost({ flight, onLanding, onDone }: {
  flight: CardFlight;
  onLanding: (id: number) => void;
  onDone: (id: number) => void;
}): ReactElement {
  const ref = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    const element = ref.current;
    if (element === null) return;
    let landingStart: number | undefined;
    // The layers are found once: they are this component's own JSX, so they live
    // exactly as long as the loop does, and a querySelector a frame was the sort
    // of per-frame cost this whole change is about removing.
    const shadow = element.querySelector<HTMLElement>('.cards-return-shadow');
    const handFace = element.querySelector<HTMLElement>('.cards-return-face-hand');
    const handName = handFace?.querySelector<HTMLElement>('.cards-hand-name') ?? null;
    const compactFace = element.querySelector<HTMLElement>('.cards-return-face-compact');
    const back = element.querySelector<HTMLElement>('.cards-return-back');
    const turnInstantly = globalThis.matchMedia?.('(prefers-reduced-motion: reduce)').matches === true;
    return startCardDragAnimation(element, () => {
      const motion = flight.motion;
      const now = globalThis.performance?.now() ?? Date.now();
      // Track the destination live: the hand keeps re-flowing (FLIP) and a
      // staged card may only appear once the server acknowledges the plan.
      // Only the box is re-read per frame; the element behind it is looked up
      // once and then reused for as long as it stays in the document and
      // uncarried, so a flight costs one measurement a frame instead of a
      // selector scan of the whole page.
      let rect: DOMRect | undefined;
      let stagedTarget = false;
      const measure = (candidate: HTMLElement): DOMRect | undefined => {
        if (!candidate.isConnected || candidate.dataset.carried === 'true') return undefined;
        const measured = candidate.getBoundingClientRect();
        return measured.width > 0 && measured.height > 0 ? measured : undefined;
      };
      const cached = flight.target;
      if (cached !== undefined) {
        rect = measure(cached.element);
        stagedTarget = cached.compact;
        // React can swap the destination out mid-flight (the hand reconciles its
        // final order on release), so a stale element falls back to the scan.
        if (rect === undefined) flight.target = undefined;
      }
      if (rect === undefined && typeof document.querySelector === 'function') {
        stagedTarget = false;
        for (const selector of flight.targetSelectors) {
          const candidate = document.querySelector<HTMLElement>(selector);
          if (candidate === null) continue;
          const measured = measure(candidate);
          if (measured === undefined) continue;
          rect = measured;
          // Board occupants share the staged card's compact glyph+stats look.
          stagedTarget = isCompactCardFlightTarget(selector);
          flight.target = { element: candidate, compact: stagedTarget };
          break;
        }
      }
      if (rect !== undefined) {
        flight.noTargetSince = undefined;
        setCardDragTarget(motion, rect.left + rect.width / 2, rect.bottom + flight.anchorGap);
        flight.toWidth = rect.width;
        flight.toHeight = rect.height;
      } else if (flight.noTargetSince === undefined) {
        flight.noTargetSince = now;
      }
      const distance = Math.hypot(motion.targetX - motion.x, motion.targetY - motion.y);
      const speed = Math.hypot(motion.velocityX, motion.velocityY);
      // Travel progress only advances while the destination actually exists.
      // A battle placement waits out the server ack with no staged card in the
      // DOM: the spring then idles on the release point (distance ≈ 0), and an
      // unguarded first-frame measurement would lock startDistance at ~1 and
      // saturate the progress — so the box would snap to every later target
      // measurement instead of morphing along the flight.
      if (rect !== undefined) {
        if (flight.startDistance < 0) flight.startDistance = Math.max(distance, 1);
        // Monotonic travel progress drives the box morph so spring overshoot
        // never makes the card grow back.
        const travelled = 1 - Math.min(distance / flight.startDistance, 1);
        flight.sizeProgress = Math.max(flight.sizeProgress, travelled);
      }
      const targetWidth = flight.fromWidth + (flight.toWidth - flight.fromWidth) * flight.sizeProgress;
      const targetHeight = flight.fromHeight + (flight.toHeight - flight.fromHeight) * flight.sizeProgress;
      // The painted box glides toward the morph target on a time basis, so a
      // late-appearing or re-measured destination (the hand re-layouts while
      // the plan is acknowledged) eases over ~150ms instead of stepping.
      const dt = Math.min(Math.max(now - (flight.lastFrameAt ?? now), 0), 50) / 1000;
      flight.lastFrameAt = now;
      const follow = 1 - Math.exp(-18 * dt);
      flight.renderWidth += (targetWidth - flight.renderWidth) * follow;
      flight.renderHeight += (targetHeight - flight.renderHeight) * follow;
      // The frame's size reaches the layers as a scale, never as a box. Both are
      // laid out at the source footprint, so at the handover from the live preview
      // the factor is exactly 1 and the card does not step.
      let srcTransform = `translateX(-50%) scale(${
        (flight.renderWidth / Math.max(flight.fromWidth, 1)).toFixed(4)}, ${
        (flight.renderHeight / Math.max(flight.fromHeight, 1)).toFixed(4)})`;
      // A dealt card turns over on the way in. One element has one side, so the
      // face itself turns to edge-on with the back laid over it, and past edge-on
      // it comes round from the other side (θ − 180°) with the back gone: the
      // angle jumps only where the card has no width, so nothing pops. The plate
      // under it turns with it, or it would stand there as a flat white card
      // while the face is edge-on.
      if (flight.dealt) {
        const turned = turnInstantly ? 180 : dealTurn(now - flight.launchedAt);
        const faceUp = turned > 90;
        srcTransform += ` perspective(520px) rotateY(${(faceUp ? turned - 180 : turned).toFixed(2)}deg)`;
        if (back !== null) back.style.opacity = faceUp ? '0' : '1';
      }
      if (handFace !== null) handFace.style.transform = srcTransform;
      if (shadow !== null) {
        shadow.style.transform = srcTransform;
        // The airborne drop shadow dissipates along the travel so the parked card
        // sits flat like its resting neighbours. Fading the whole plate is safe
        // because an opaque face covers it the entire way down.
        shadow.style.opacity = (1 - flight.sizeProgress).toFixed(3);
      }
      // Flying to a staged cell, the ghost sheds its hand-only chrome along the
      // way: faceProgress (0→1, eased on travel) brings up the compact field face
      // over the detailed hand one, so the card has already become the glyph+stats
      // look by the time it lands instead of popping at touchdown. No colour change
      // (both faces wear the same card skin). Hand returns keep it at 0, and the
      // easing stops a mid-flight target switch from popping it. A flight with no
      // compact destination has no second face at all (see canLandCompact).
      // Eased on elapsed time, not per frame (03 §7): the old 0.22 a frame ran
      // twice as fast on a 120 Hz screen. 15/s is that rate at 60 Hz.
      if (flight.canLandCompact) {
        const compactness = stagedTarget ? 1 : 0;
        const faceFollow = 1 - Math.exp(-FACE_FOLLOW_RATE * dt);
        flight.faceProgress += (flight.sizeProgress * compactness - flight.faceProgress) * faceFollow;
      }
      if (compactFace !== null) {
        // The compact face is laid out at the DESTINATION's box, so its own
        // padding, border and container query resolve to the landed card's — the
        // one thing a scale cannot fake. Following the measurement in steps (and
        // only real ones) keeps that to a handful of layouts per flight: a staged
        // card appears late, the hand re-flows, and neither happens per frame.
        if (Math.abs(flight.toWidth - flight.compactWidth) > 0.5) {
          flight.compactWidth = flight.toWidth;
          element.style.setProperty('--cards-return-dst-w', `${flight.toWidth.toFixed(1)}px`);
        }
        if (Math.abs(flight.toHeight - flight.compactHeight) > 0.5) {
          flight.compactHeight = flight.toHeight;
          element.style.setProperty('--cards-return-dst-h', `${flight.toHeight.toFixed(1)}px`);
        }
        compactFace.style.transform = `translateX(-50%) scale(${
          (flight.renderWidth / Math.max(flight.compactWidth, 1)).toFixed(4)}, ${
          (flight.renderHeight / Math.max(flight.compactHeight, 1)).toFixed(4)})`;
        compactFace.style.opacity = flight.faceProgress.toFixed(3);
        // The name is the one piece of the hand face with nowhere to go: the cost
        // badge and the stat strip fade under the compact face's own badge and
        // numbers, but the label lies across the middle of a card that is turning
        // landscape, so for the first half of the morph it drags over the growing
        // glyph. Gone by ~a third of the travel, before the box has changed enough
        // for the overlap to read.
        if (handName !== null) {
          handName.style.opacity = Math.max(0, 1 - flight.faceProgress * 3).toFixed(3);
        }
      }
      // The fade waits for a genuine stop (parked on the destination, spring
      // calm, box converged), otherwise the card dissolves mid-drift or
      // mid-resize like a ghost. A card with no destination (a cast spell)
      // parks in place instead.
      const parked = (rect !== undefined && distance < 2 && speed < 12
          && Math.abs(flight.renderWidth - flight.toWidth) < 2
          && Math.abs(flight.renderHeight - flight.toHeight) < 2)
        || now > flight.deadline
        || (!flight.waitForTarget
          && flight.noTargetSince !== undefined
          && now - flight.noTargetSince > FLIGHT_NO_TARGET_MS);
      if (landingStart === undefined && parked) {
        landingStart = now;
        onLanding(flight.id);
      }
      if (landingStart !== undefined) {
        const faded = (now - landingStart) / FLIGHT_LANDING_FADE_MS;
        if (faded >= 1) {
          onDone(flight.id);
          return undefined;
        }
        element.style.opacity = (1 - faded).toFixed(3);
      }
      return motion;
    });
  }, [flight, onLanding, onDone]);
  return (
    <div
      ref={ref}
      className="cards-touch-drag-preview"
      data-testid="return-flight-preview"
      data-return-flight="true"
      data-return-morph="true"
      data-card-type={flight.card.type}
      aria-hidden="true"
      // The carrier's own box is the release footprint and never changes; the two
      // face sizes below are what the layers are laid out at (the destination pair
      // is re-written by the loop when the destination is re-measured). Position
      // rides --cards-drag-x/y in the transform chain; static presentation stays
      // in KIT_CSS.
      style={{
        '--cards-return-src-w': `${flight.fromWidth.toFixed(1)}px`,
        '--cards-return-src-h': `${flight.fromHeight.toFixed(1)}px`,
        '--cards-return-dst-w': `${flight.compactWidth.toFixed(1)}px`,
        '--cards-return-dst-h': `${flight.compactHeight.toFixed(1)}px`,
        '--cards-return-gap': `${flight.anchorGap.toFixed(1)}px`,
        ...cardDragMotionVariables(flight.motion),
      } as CSSProperties}
    >
      {/* The airborne step (step-lift), on its own layer so it rasterises once
         instead of on every frame of the flight. */}
      <div className="cards-return-shadow" aria-hidden="true" />
      {/* Detailed hand face + a compact field face that comes up over it. The
         compact face reuses the field card's own classes — raised glyph, name row,
         edge-anchored stat strip — so it is pixel-identical to the staged/board
         card the ghost lands on. The type is on both: it decides the frame colour
         and, on the compact face, whether there is a name row at all. */}
      <div className="cards-return-face-hand" data-card-type={flight.card.type} aria-hidden="true">
        <HandCardFace
          defId={flight.card.instance.defId}
          cost={flight.card.cost}
          type={flight.card.type}
          hasStats={flight.card.hasStats}
          attack={flight.card.instance.stats.attack}
          health={flight.card.instance.stats.health}
          nameAnimated={false}
        />
        {/* The same back a face-down board card shows, until the turn passes
           edge-on (the loop fades it on that frame). */}
        {flight.dealt ? (
          <CardBackFace patternId={`flight-${flight.id}-back-pattern`} className="cards-return-back" />
        ) : null}
      </div>
      {/* Absent entirely when no candidate destination is a compact one: it would
         spend the whole flight at opacity 0 and still be a second full card, second
         glyph included, for the browser to lay out. */}
      {flight.canLandCompact ? (
        <div className="cards-return-face-compact" data-card-type={flight.card.type} aria-hidden="true">
          <div className="cards-staged-card-art">
            <CardGlyph defId={flight.card.instance.defId} size={60} />
          </div>
          <ScrollingCardName
            name={cardName(flight.card.instance.defId)}
            className="cards-board-card-name"
            trackClassName="cards-board-card-name-track"
            animate={false}
          />
          {flight.card.hasStats ? (
            <div className="cards-staged-card-stats">
              <StatChip icon="attack" value={flight.card.instance.stats.attack} len={statLen(flight.card.instance.stats.attack)} aria-hidden="true" />
              <StatChip icon="health" value={flight.card.instance.stats.health} len={statLen(flight.card.instance.stats.health)} aria-hidden="true" />
            </div>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}

/**
 * Measures a live drag-preview element into a flight origin (with fallbacks).
 *
 * The size comes off the UNTRANSFORMED border box and only the anchor comes off
 * the rect. The preview is on screen under the drag spring's scale plus its own
 * +.04 bonus, and a rotated, skewed, perspective-projected box reports its
 * axis-aligned bounding box — some 14% taller than the card really is. Handing
 * that to the flight applied the scale twice, because the ghost inherits the
 * same motion and scales its box again: the card visibly jumped bigger at the
 * preview→flight handover and then had that much more box to shrink away.
 * offsetWidth/offsetHeight are the layout box the preview's own CSS asked for
 * (112×138 desktop, 84×96 mobile), so the scale now travels exactly once, in
 * the motion.
 */
export function flightOriginFromPreview(
  preview: HTMLElement | null,
  motion: CardDragMotion,
  fallbackWidth = 112,
  fallbackHeight = 138,
): FlightOrigin {
  const rect = preview?.getBoundingClientRect();
  const width = preview?.offsetWidth ?? 0;
  const height = preview?.offsetHeight ?? 0;
  return {
    width: width > 0 ? width : fallbackWidth,
    height: height > 0 ? height : fallbackHeight,
    anchorGap: rect === undefined ? PREVIEW_ANCHOR_GAP : Math.max(0, motion.y - rect.bottom),
  };
}

export interface CardFlightApi {
  /** Launch (or replace) the flight for a released card. */
  launch: (instanceId: InstanceId, motion: CardDragMotion, origin: FlightOrigin) => void;
  /** Drop a card's flight (it was picked back up). */
  cancel: (instanceId: InstanceId) => void;
  /** Cards currently airborne — their resting representation stays hidden. */
  flyingIds: ReadonlySet<InstanceId>;
  /** Cards whose flight is parked and dissolving — un-hide instantly underneath. */
  landingIds: ReadonlySet<InstanceId>;
}
